import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import './styles/sequentialTestFlow.css';

// 修改测试场景ID以匹配JSON文件中实际存在的ID
const TEST_SCENARIO_IDS = [
  'workplace_social_1',
  'workplace_social_2',
  'workplace_social_3',
  'workplace_social_4',
  'workplace_social_5',
  'workplace_social_6'
];

function SequentialTestFlow() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testScenarios, setTestScenarios] = useState([]);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [currentAnswers, setCurrentAnswers] = useState({});
  const [allAnswers, setAllAnswers] = useState({});
  const [testCompleted, setTestCompleted] = useState(false);
  const [alreadyCompletedTest, setAlreadyCompletedTest] = useState(false);
  
  const { userData } = useAuth();
  const navigate = useNavigate();
  
  // 检查是否完成了问卷调查
  useEffect(() => {
    const checkQuestionnaireCompletion = () => {
      const questionnaireData = localStorage.getItem('personalQuestionnaireData');
      if (!questionnaireData) {
        console.log("No questionnaire data found, redirecting to questionnaire page");
        navigate('/personal-questionnaire');
        return false;
      }
      return true;
    };
    
    // 检查是否已经完成了测试
    const checkTestCompletion = () => {
      const testResultsData = localStorage.getItem('testResults');
      if (testResultsData) {
        try {
          const parsedResults = JSON.parse(testResultsData);
          // 如果有测试结果，则显示已完成页面
          if (parsedResults && parsedResults.length > 0) {
            console.log("Test already completed, showing completion page");
            setAlreadyCompletedTest(true);
            setLoading(false);
            return true;
          }
        } catch (err) {
          console.error("Error parsing test results:", err);
        }
      }
      return false;
    };
    
    const hasQuestionnaire = checkQuestionnaireCompletion();
    if (hasQuestionnaire) {
      checkTestCompletion();
    }
  }, [navigate]);
  
  // 加载测试场景数据
  useEffect(() => {
    // 如果已经完成测试，不需要加载场景数据
    if (alreadyCompletedTest) {
      return;
    }
    
    async function loadTestScenarios() {
      try {
        setLoading(true);
        setError('');
        
        // 直接指定正确的路径
        const url = '/combinedScenariosData.json';
        
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to load data: ${response.status} ${response.statusText}`);
          }
          
          const data = await response.json();
          console.log(`Successfully loaded test data from ${url}`);
          
          // 获取测试场景数据
          const allScenarios = data.scenarios || [];
          
          // 筛选出测试场景
          const testScenariosData = TEST_SCENARIO_IDS.map(id => {
            const foundScenario = allScenarios.find(s => s.id === id);
            if (!foundScenario) {
              console.warn(`Test scenario with ID ${id} not found`);
            }
            return foundScenario;
          }).filter(Boolean);
          
          if (testScenariosData.length === 0) {
            throw new Error('No test scenarios found. Please check data configuration');
          }
          
          console.log(`Successfully loaded ${testScenariosData.length} test scenarios`);
          setTestScenarios(testScenariosData);
        } catch (e) {
          console.error(`Failed to load test data:`, e);
          throw e;
        }
      } catch (err) {
        console.error("Error loading test scenarios:", err);
        setError(err.message || 'Failed to load test scenarios. Please refresh and try again');
        // 如果加载失败，延迟2秒后重定向到问卷页面
        setTimeout(() => {
          navigate('/personal-questionnaire');
        }, 2000);
      } finally {
        setLoading(false);
      }
    }
    
    loadTestScenarios();
  }, [alreadyCompletedTest, navigate]);
  
  // 处理答案选择
  const handleAnswerSelect = (optionValue) => {
    // 获取当前场景
    const currentScenario = testScenarios[currentScenarioIndex];
    if (!currentScenario) return;
    
    // 更新当前场景的答案
    setCurrentAnswers({
      scenarioId: currentScenario.id,
      selectedOption: optionValue
    });
  };
  
  // 提交当前答案并前进到下一题
  const handleNext = () => {
    if (!currentAnswers.selectedOption) {
      alert('Please select an answer');
      return;
    }
    
    // 添加到全部答案中
    setAllAnswers(prev => ({
      ...prev,
      [currentAnswers.scenarioId]: currentAnswers.selectedOption
    }));
    
    // 清空当前答案
    setCurrentAnswers({});
    
    // 检查是否完成所有题目
    if (currentScenarioIndex >= testScenarios.length - 1) {
      setTestCompleted(true);
    } else {
      // 前进到下一题
      setCurrentScenarioIndex(prev => prev + 1);
    }
  };
  
  // 完成测试，计算分数并保存结果
  const completeTest = () => {
    // 计算分数
    let totalScore = 0;
    let maxPossibleScore = 0;
    
    testScenarios.forEach(scenario => {
      const userAnswer = allAnswers[scenario.id];
      if (userAnswer && scenario.options) {
        // 找到用户选择的选项
        const selectedOption = scenario.options.find(opt => opt.value === userAnswer);
        if (selectedOption) {
          totalScore += selectedOption.score || 0;
        }
        
        // 计算最高可能分数
        const maxOption = scenario.options.reduce((max, opt) => {
          return (opt.score > max.score) ? opt : max;
        }, { score: 0 });
        maxPossibleScore += maxOption.score || 0;
      }
    });
    
    // 计算百分比分数
    const percentageScore = Math.round((totalScore / maxPossibleScore) * 100);
    
    // 保存测试结果到本地存储
    const testResults = {
      timestamp: new Date().toISOString(),
      scenarios: testScenarios.map(s => s.id),
      answers: allAnswers,
      score: totalScore,
      maxScore: maxPossibleScore,
      percentageScore,
      userId: userData?.id || 'anonymous'
    };
    
    try {
      // 获取现有结果
      const existingResultsJSON = localStorage.getItem('testResults');
      const existingResults = existingResultsJSON ? JSON.parse(existingResultsJSON) : [];
      
      // 添加新结果
      existingResults.push(testResults);
      
      // 保存回本地存储
      localStorage.setItem('testResults', JSON.stringify(existingResults));
      
      // 导航到结果页
      navigate('/test-result', { 
        state: { 
          testResults,
          fromSequentialTest: true 
        }
      });
    } catch (err) {
      console.error('保存测试结果失败:', err);
      alert('保存测试结果时出错，请重试');
    }
  };
  
  if (loading) {
    return (
      <div className="loading-indicator">
        <div className="spinner"></div>
        <p>Loading test scenarios...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <h3>Loading Error</h3>
        <p>{error}</p>
        <button className="primary-button" onClick={() => navigate('/')}>Return to Home</button>
      </div>
    );
  }
  
  // 显示已完成测试的页面
  if (alreadyCompletedTest) {
    return (
      <div className="test-already-completed">
        <div className="completed-message-container">
          <h2>Test Already Completed</h2>
          <p>You have already completed the EQ assessment test. You can view your personalized report or return to the home page.</p>
          
          <div className="action-buttons">
            <button 
              className="primary-button"
              onClick={() => navigate('/analysis-report')}
            >
              View Your Report
            </button>
            <button 
              className="secondary-button"
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (testCompleted) {
    return (
      <div className="test-completion">
        <h2>Test Completed!</h2>
        <p>You have completed all test scenarios. Click the button below to view your results.</p>
        <button className="primary-button" onClick={completeTest}>View Results</button>
      </div>
    );
  }
  
  const currentScenario = testScenarios[currentScenarioIndex];
  if (!currentScenario) {
    return (
      <div className="error-container">
        <h3>Scenario Not Found</h3>
        <p>The current test scenario cannot be found. Please return to the home page and try again.</p>
        <button className="primary-button" onClick={() => navigate('/')}>Return to Home</button>
      </div>
    );
  }
  
  return (
    <div className="test-flow-container">
      <div className="test-progress">
        <div className="progress-text">
          Scenario {currentScenarioIndex + 1} / {testScenarios.length}
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${((currentScenarioIndex) / testScenarios.length) * 100}%` }}
          ></div>
        </div>
      </div>
      
      <div className="test-content">
        <h2>{currentScenario.title_en}</h2>
        <p className="scenario-description">{currentScenario.scenario_en}</p>
        
        <div className="question">
          <h3>How would you respond in this situation?</h3>
          
          <div className="options">
            {/* 创建模拟选项，因为原始数据可能不包含选项 */}
            {generateOptions(currentScenario).map((option, index) => (
              <div 
                key={option.value} 
                className={`option ${currentAnswers.selectedOption === option.value ? 'selected' : ''}`}
                onClick={() => handleAnswerSelect(option.value)}
              >
                <div className="option-text">{option.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="test-actions">
        <button 
          className="primary-button" 
          onClick={handleNext}
          disabled={!currentAnswers.selectedOption}
        >
          {currentScenarioIndex < testScenarios.length - 1 ? 'Next' : 'Complete Test'}
        </button>
      </div>
    </div>
  );
}

// 生成选项函数，如果场景没有选项则创建模拟选项
function generateOptions(scenario) {
  // 如果场景已经有选项，使用它们
  if (scenario.options && Array.isArray(scenario.options) && scenario.options.length > 0) {
    return scenario.options;
  }
  
  // 否则，基于场景内容创建模拟选项
  return [
    {
      value: 'option_a',
      text: 'Address the situation directly by having an open conversation about the issue.',
      score: 8
    },
    {
      value: 'option_b',
      text: 'Consult with a trusted colleague or mentor before taking any action.',
      score: 6
    },
    {
      value: 'option_c',
      text: 'Observe the situation further to gather more information before responding.',
      score: 7
    },
    {
      value: 'option_d',
      text: 'Use written communication to clearly express your thoughts and concerns.',
      score: 5
    }
  ];
}

export default SequentialTestFlow;
